import { FC, useCallback, useMemo, useState } from 'react';

import { capitalise, dayjs } from '@tsl-frontend/tsl-utilities';
import {
    Box,
    Chip,
    GridColDef,
    GridSortModel,
    TSLLink,
    Typography,
} from '@thirdspacelearning/library';

import { GBProgramme, ProgrammeType, USProgramme } from '../../types';
import { statusMap, yearGroupSortComparator } from '../../utils';

import { CMSDataGrid, useDataGridToolbar } from '@shared';
import { Country } from '@types';

const sx = {
    root: { display: 'flex', flexDirection: 'column', gap: 2, mt: 2 },
};

type Props = {
    programmes: ProgrammeType[];
    country: Country;
};

const ProgrammesTable: FC<Props> = ({ programmes, country }) => {
    const { filterPanelEl, slots, slotProps } = useDataGridToolbar();

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'id',
            sort: 'desc',
        },
    ]);

    const gbColumns = useMemo(
        (): GridColDef<ProgrammeType>[] =>
            [
                {
                    field: 'keyStage',
                    type: 'string',
                    headerName: 'Key Stage',
                    valueGetter: (value, row) =>
                        (row as GBProgramme).key_stage ?? '-',
                    renderCell: ({ row }) =>
                        (row as GBProgramme).key_stage ? (
                            <Chip
                                label={`Key Stage ${(row as GBProgramme).key_stage}`}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: true,
                    minWidth: 120,
                    flex: 1,
                },
                {
                    field: 'yearGroup',
                    type: 'string',
                    headerName: 'Year group',
                    sortingOrder: ['asc', 'desc'],
                    sortComparator: yearGroupSortComparator,
                    valueGetter: (value, row) => row.year_id,
                    renderCell: ({ row }) =>
                        row.year_id ? (
                            <Chip
                                label={`Year ${row.year_id}`}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: true,
                    minWidth: 100,
                    flex: 1,
                },
            ] as GridColDef<ProgrammeType>[],
        [],
    );

    const usColumns = useMemo(
        (): GridColDef<ProgrammeType>[] =>
            [
                {
                    field: 'stateStandard',
                    type: 'string',
                    headerName: 'State Standard',
                    valueGetter: (value, row) =>
                        (row as USProgramme).state_standard,
                    renderCell: ({ row }) =>
                        (row as USProgramme).state_standard ? (
                            <Chip
                                label={(row as USProgramme).state_standard}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: true,
                    minWidth: 100,
                    flex: 1,
                },
                {
                    field: 'grade',
                    type: 'string',
                    headerName: 'Grade',
                    sortingOrder: ['asc', 'desc'],
                    sortComparator: yearGroupSortComparator,
                    valueGetter: (value, row) => row.year_id,
                    renderCell: ({ row }) =>
                        row.year_id ? (
                            <Chip
                                label={`Grade ${row.year_id}`}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: true,
                    minWidth: 130,
                    flex: 0.5,
                },
            ] as GridColDef<ProgrammeType>[],
        [],
    );

    const columns = useMemo(
        (): GridColDef<ProgrammeType>[] => [
            {
                field: 'id',
                type: 'string',
                headerName: 'ID',
                headerAlign: 'left',
                align: 'left',
                sortable: true,
                maxWidth: 70,
            },
            {
                field: 'programmeName',
                type: 'string',
                headerName: 'Programme name',
                valueGetter: (value, row) => row.title,
                renderCell: ({ row }) => (
                    <TSLLink
                        to={`${row.id}`}
                        variant="body2"
                        data-testid="programme-link"
                    >
                        {row.title}
                    </TSLLink>
                ),
                sortable: true,
                minWidth: 250,
                flex: 2,
            },
            ...(country === Country.GB ? gbColumns : usColumns),
            {
                field: 'created_at',
                type: 'date',
                headerName: 'Created date',
                valueFormatter: (value) =>
                    dayjs(value).format('DD/MM/YY HH:mm'),
                renderCell: ({ row }) => (
                    <Typography variant="caption">
                        {dayjs(row.created_at).format('DD/MM/YY HH:mm')}
                    </Typography>
                ),
                sortingOrder: ['asc', 'desc'],
                sortable: true,
                minWidth: 100,
                flex: 1,
            },
            {
                field: 'updated_at',
                type: 'date',
                headerName: 'Last modified',
                valueFormatter: (value) =>
                    dayjs(value).format('DD/MM/YY HH:mm'),
                renderCell: ({ row }) => (
                    <Typography variant="caption">
                        {dayjs(row.updated_at).format('DD/MM/YY HH:mm')}
                    </Typography>
                ),
                sortingOrder: ['asc', 'desc'],
                sortable: true,
                minWidth: 100,
                flex: 1,
            },
            {
                field: 'status',
                type: 'string',
                headerName: 'Status',
                valueGetter: (value, row) => row.status,
                renderCell: ({ row }) =>
                    row.status ? (
                        <Chip
                            label={capitalise(row.status)}
                            color={statusMap[row.status]}
                            size="small"
                        />
                    ) : (
                        '-'
                    ),
                sortable: true,
                minWidth: 100,
                flex: 1,
            },
        ],
        [country, gbColumns, usColumns],
    );

    const handleSortModelChange = useCallback((model: GridSortModel) => {
        setSortModel(model);
    }, []);

    return (
        <Box sx={sx.root}>
            <Box id="filter-panel" ref={filterPanelEl} />
            <CMSDataGrid
                columns={columns}
                rows={programmes}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                slots={slots}
                slotProps={slotProps}
                showToolbar
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                disableColumnResize
                hideFooter
            />
        </Box>
    );
};

export default ProgrammesTable;
